import { parseISO, compareAsc } from 'date-fns';
import { IAttendanceResponse } from '@/entities/Attendance';
import { convertIsoToDate, convertIsoToTime } from '@/shared/lib/utils/dateAdapters';

export interface IAttendanceSession {
  scheduledAt: string;
  time: string;
  attendance: number | null;
  title: string;
  groupLessonId?: number;
  isRequestAllowed?: boolean;
  requestBlockReason?: string | null;
}

export interface IAttendanceDateColumn {
  date: string;
  dateFormatted: string;
  sessions: IAttendanceSession[];
}

export interface IAttendanceTermData {
  courseTitle: string;
  termNumber: number;
  totalLessons: number;
  attendedSessions: number;
  attendancePercent: number | null;
  lastTermAbsence: string | null;
  dateColumns: IAttendanceDateColumn[];
}

const getAttendancePercent = (attendance: number | null | undefined) => {
  if (attendance === null || attendance === undefined) return null;
  return Math.round(attendance <= 1 ? attendance * 100 : attendance);
};

export const transformAttendanceData = (
  data: IAttendanceResponse | undefined,
): IAttendanceTermData[] => {
  const result: IAttendanceTermData[] = [];

  if (!data || !data.courses || !Array.isArray(data.courses)) {
    return result;
  }

  data.courses.forEach((course) => {
    (course.terms ?? []).forEach((term) => {
      const allSessions: Array<{
        scheduledAt: string;
        attendance: number | null;
        title: string;
        groupLessonId?: number;
        isRequestAllowed?: boolean;
        requestBlockReason?: string | null;
      }> = [];

      (term.modules ?? []).forEach((module) => {
        (module.lessons ?? []).forEach((lesson) => {
          if (lesson.scheduledAt) {
            allSessions.push({
              scheduledAt: lesson.scheduledAt,
              attendance: lesson.attendance,
              title: lesson.title,
              groupLessonId: lesson.groupLessonId,
              isRequestAllowed: lesson.isRequestAllowed,
              requestBlockReason: lesson.requestBlockReason,
            });
          }
        });
      });

      const totalLessons = allSessions.length;
      const attendedSessions = allSessions.filter(
        (s) => s.attendance !== null && s.attendance !== 0,
      ).length;

      const absenceDates = allSessions
        .filter((s) => s.attendance === 0)
        .map((s) => s.scheduledAt)
        .filter((date): date is string => date !== null)
        .sort((a, b) => compareAsc(parseISO(b), parseISO(a)));

      const lastTermAbsence = absenceDates.length > 0 ? convertIsoToDate(absenceDates[0]) : null;

      const dateMap = new Map<string, IAttendanceSession[]>();

      allSessions.forEach((session) => {
        if (session.scheduledAt) {
          const dateKey = convertIsoToDate(session.scheduledAt);
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, []);
          }
          dateMap.get(dateKey)!.push({
            scheduledAt: session.scheduledAt,
            time: convertIsoToTime(session.scheduledAt),
            attendance: session.attendance,
            title: session.title,
            groupLessonId: session.groupLessonId,
            isRequestAllowed: session.isRequestAllowed,
            requestBlockReason: session.requestBlockReason,
          });
        }
      });

      const dateColumns: IAttendanceDateColumn[] = Array.from(dateMap.entries())
        .map(([dateFormatted, sessions]) => {
          const firstSession = sessions[0];
          const dateKey = firstSession.scheduledAt;
          return {
            date: dateKey,
            dateFormatted,
            sessions: sessions.sort((a, b) => a.time.localeCompare(b.time)),
          };
        })
        .sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));

      result.push({
        courseTitle: course.title,
        termNumber: term.number,
        totalLessons,
        attendedSessions,
        attendancePercent: getAttendancePercent(course.attendance),
        lastTermAbsence,
        dateColumns,
      });
    });
  });

  return result;
};
